import { Component, Prop, h, State, Watch, Element, EventEmitter, Event } from '@stencil/core';

export interface IDataHeader {
  title?: string;
  icon?: string;
  type?: 'text' | 'number' | 'badge' | 'badge' | 'select' | 'quantify' | 'customize';
  colKey: string;
  positionTitle?: 'left' | 'right';
  sortable?: boolean;
  customSort?: boolean;
}

export interface IBadgeProps {
  label: string;
  variant?: 'outlined' | 'ghost' | 'dot';
  size?: 'small' | 'medium';
  state?: 'success' | 'error' | 'warning';
}

export interface ISelectProps {
  options: Array<{ label: string; isChecked: boolean; key: string; nameAvatar?: string; imgSRCAvatar?: string }>;
  optionsSelected?: Array<{ label: string; isChecked: boolean; key: string; nameAvatar?: string; imgSRCAvatar?: string }>;
  type?: 'checkbox' | 'radioButton';
  avatar?: boolean;
  value?: string;
  isSearchable?: boolean;
  titleInput?: string;
  label?: string;
  placeholderSearch?: string;
  disabled?: boolean;
  helperText?: string;
  state?: 'error' | 'default';
  onChangeInput?: ({ detail }) => void;
  onChangeInputOptions?: ({ detail }) => void;
}

export interface IQuantifyProps {
  maxValue?: number;
  value?: number;
  state?: 'error' | 'default';
  onHandleCounterValue?: ({ detail }) => void;
}

export interface IContextMenuProps {
  items: Array<{ icon?: string; label: string; key: string; disabled?: boolean; event: () => void }>;
  destructiveItem?: { icon?: string; label: string; key: string; disabled?: boolean; event?: () => void };
}

export interface IDataRow {
  text?: {
    title?: string;
    avatar?: boolean;
    avatarImgSRC?: string;
    icon?: string;
  };
  number?: {
    title?: string;
  };
  key?: string;
  handleRow?: () => void;
  badge?: IBadgeProps;
  select?: ISelectProps;
  quantify?: IQuantifyProps;
  action?: IContextMenuProps;
  customize?: {
    component?: string;
    props?: JSX.Element;
  };
}

export interface IData {
  headers: IDataHeader[];
  rows: Record<string, IDataRow>[];
}

@Component({
  tag: 'lab-ds-table',
  styleUrl: 'lab-ds-table.css',
  shadow: false,
})
export class LabDsTable {
  @Prop() data!: IData;

  @Prop() actionField: boolean = false;

  @Prop() loading: boolean = false;

  @Event() finalLineNextPage: EventEmitter<boolean>;

  @Event() sortAsc: EventEmitter<string>;

  @Event() sortDesc: EventEmitter<string>;

  @Prop() headerPosition: 'left' | 'right' = 'left';

  @State() sortConfig: { key: string; direction: string };

  handleSortAsc(d: IDataHeader) {
    if (d?.sortable) {
      if (d.customSort) {
        this.sortAsc.emit(d.colKey);
        return;
      }
      this.sortConfig = { key: d.colKey, direction: 'asc' };
    }
  }

  handleSortDesc(d: IDataHeader) {
    if (d?.sortable) {
      if (d.customSort) {
        this.sortDesc.emit(d.colKey);
        return;
      }
      this.sortConfig = { key: d.colKey, direction: 'desc' };
    }
  }

  @Watch('sortConfig')
  sortData(sortConfig) {
    if (!sortConfig) {
      return;
    }

    const { key, direction } = sortConfig;

    this.data.rows.sort((a, b) => {
      let aSort = a[key].number?.title ? a[key].number.title : a[key].text.title;
      let bSort = b[key].number?.title ? b[key].number.title : b[key].text.title;

      if (direction === 'asc') {
        return aSort > bSort ? 1 : -1;
      }

      if (direction === 'desc') {
        return aSort < bSort ? 1 : -1;
      }
    });
  }

  // eslint-disable-next-line @stencil-community/element-type
  @Element() el: HTMLElement;

  componentDidRender() {
    const scroll: HTMLElement = this.el.querySelector('.v4-table');

    scroll.addEventListener(
      'scroll',
      () => {
        if (Math.abs(scroll.scrollHeight - scroll.clientHeight - scroll.scrollTop) < 1) {
          this.finalLineNextPage.emit(true);
        }
      },
      {
        passive: true,
      },
    );
  }

  render() {
    return (
      <div class={`v4-table scroll-customized header-${this.headerPosition}`}>
        <table>
          <thead>
            <tr class="table-header">
              {this.data?.headers?.map(d => (
                <th class="header-cell">
                  {this.headerPosition === 'right' || d.type === 'number' ? (
                    <div class="cell-container">
                      {d.sortable && (
                        <div class="cell-order">
                          <lab-ds-icon-selectable
                            onClick={() => {
                              this.handleSortAsc(d);
                            }}
                            size="x-small"
                            icon="expand_less"
                          />
                          <lab-ds-icon-selectable
                            onClick={() => {
                              this.handleSortDesc(d);
                            }}
                            size="x-small"
                            icon="expand_more"
                          />
                        </div>
                      )}
                      <div class={`cell-content ${d.type === 'number' ? 'cell-number' : ''} ${d.sortable ? 'sortable' : ''}`}>
                        {d.icon && !d.sortable && <lab-ds-icon-not-selectable class="header-cell-icon" size="small" icon={d.icon} />}
                        <span class="header-title">{d.title}</span>
                      </div>
                    </div>
                  ) : (
                    <div class="cell-container">
                      <div class="cell-content">
                        <span class="header-title">{d.title}</span>
                        {d.icon && !d.sortable && <lab-ds-icon-not-selectable class="header-cell-icon" size="small" icon={d.icon} />}
                      </div>
                      {d.sortable && (
                        <div class="cell-order">
                          <lab-ds-icon-selectable
                            onClick={() => {
                              this.handleSortAsc(d);
                            }}
                            size="x-small"
                            icon="expand_less"
                          />
                          <lab-ds-icon-selectable
                            onClick={() => {
                              this.handleSortDesc(d);
                            }}
                            size="x-small"
                            icon="expand_more"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </th>
              ))}
              {this.actionField && <th class="header-cell"></th>}
            </tr>
          </thead>
          {!this.loading && (
            <tbody>
              {this.data?.rows?.map((row, index) => (
                <tr key={index} tabIndex={0} class="row">
                  {this.data.headers.map(header => (
                    <td
                      onClick={e => {
                        if (header.type === 'text' || 'number') {
                          e.preventDefault();
                          row[header.colKey]?.handleRow && row[header.colKey]?.handleRow();
                        } else {
                          return;
                        }
                      }}
                      style={{ width: `${100 / this.data.headers.length}%` }}
                      class={`cell-row ${header.type === 'text' || 'number' ? 'cell-row-clickable' : ''}`}
                    >
                      {header.type === 'text' && (
                        <div class="cell-row-container">
                          <div class="cell-row-container-avatar">
                            {row[header.colKey]?.text?.avatar && <lab-ds-avatar size="small" imageSRC={row[header.colKey]?.text?.avatarImgSRC} />}
                            <div class="cell-row-container-text">
                              <span title={row[header.colKey]?.text?.title || '-'} class="cell-row-text">
                                {row[header.colKey]?.text?.title || '-'}
                              </span>
                            </div>
                          </div>
                          <div class="cell-row-text-icon">
                            {row[header.colKey]?.text?.icon && <lab-ds-icon-not-selectable class="cell-row-text-icon" icon={row[header.colKey]?.text?.icon} size="small" />}
                          </div>
                        </div>
                      )}

                      {header.type === 'number' && <span class="cell-row-number">{row[header.colKey]?.number?.title || '-'}</span>}

                      {header.type === 'badge' && (
                        <div class="cell-row-components">
                          <lab-ds-badge {...row[header.colKey]?.badge} />
                        </div>
                      )}

                      {header.type === 'select' && (
                        <div class="cell-row-components">
                          <lab-ds-select style={{ width: '100%' }} {...row[header.colKey]?.select} />
                        </div>
                      )}

                      {header.type === 'quantify' && (
                        <div class="cell-row-components">
                          <lab-ds-quantify {...row[header.colKey]?.quantify} />
                        </div>
                      )}
                      {header.type === 'customize' && (
                        <div class="cell-row-components">
                          <div {...row[header.colKey]?.customize?.props.props} innerHTML={row[header.colKey]?.customize.component}></div>
                        </div>
                      )}
                    </td>
                  ))}
                  {this.actionField && (
                    <td class="cell-row">
                      <lab-ds-context-menu listPosition="right" {...row?.action?.action} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          )}
        </table>
        {this.loading && (
          <div class="table-loading">
            <lab-ds-skeleton />
          </div>
        )}
      </div>
    );
  }
}
